import { TagMutedRounded } from '@components/core-ui/Tag/variations/TagMutedRounded';
import { getAmericanTime } from '@helpers/getAmericanTimezone';
import { useModalContext } from '@providers/ModalProvider';
import { useAPI } from '@providers/useAPI';
import { useMutation, useQuery } from '@tanstack/react-query';
import { PipelineAttemptsService, PipelineEventDto } from 'app/API';
import { adminJobsKeys } from 'app/queries/adminJobs/adminJobsKeys';
import { ModalNamesEnum } from 'app/types/enums/ModalNamesEnum';
import { useMemo, useRef } from 'react';
import toast from 'react-hot-toast';

import { createFetchAttemptLogsQueryFn } from '../../../../../../api/fetchAttemptLogs';
import { parseAttemptLogLines } from '../../../../../../helpers/parseAttemptLogLines';
import { usePageContext } from '../../../../../../providers/PageProvider';
import { FullWidthSectionRight } from '../../styles/FullWidthSectionRight';
import { JobSection } from '../../styles/JobSection';
import { JobSectionLeft } from '../../styles/JobSectionLeft';
import { LiveExecutionPanel } from './components/LiveExecutionPanel';
import { formatEtaMs, formatServiceLabel, formatStageLabel, isDispatchStage } from './helpers/pipelineDisplay';
import { PipelineStatusTag } from './helpers/statusBadgeTag';

const formatDateTime = (value?: string | null) => {
  if (!value) {
    return '---';
  }
  return getAmericanTime(value, true, true);
};

const formatDuration = (duration?: number | null) => {
  if (duration === undefined || duration === null) {
    return '---';
  }
  const totalSeconds = Math.max(0, Math.round(duration));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  const segments: string[] = [];
  if (hours) {
    segments.push(`${hours}h`);
  }
  if (minutes) {
    segments.push(`${minutes}m`);
  }
  if (!segments.length || seconds) {
    segments.push(`${seconds}s`);
  }
  return segments.join(' ');
};

const isSuccessfulStatus = (status?: string | null) => {
  if (!status) {
    return false;
  }
  const normalized = status.toLowerCase();
  return normalized.includes('success') || normalized.includes('complete');
};

const eventDotColor = (status?: string | null) => {
  if (!status) {
    return 'bg-[var(--color-surface-muted)]';
  }
  const normalized = status.toLowerCase();
  if (normalized.includes('fail') || normalized.includes('error')) {
    return 'bg-red-400';
  }
  if (normalized.includes('success') || normalized.includes('complete')) {
    return 'bg-emerald-400';
  }
  return 'bg-indigo-400';
};

const eventDurationSeconds = (event: PipelineEventDto) => {
  if (!event.startedAt || !event.completedAt) {
    return undefined;
  }
  const start = new Date(event.startedAt).getTime();
  const end = new Date(event.completedAt).getTime();
  if (Number.isNaN(start) || Number.isNaN(end) || end < start) {
    return undefined;
  }
  return Math.round((end - start) / 1000);
};

const normalizeProgress = (progress?: number) => {
  if (typeof progress !== 'number' || Number.isNaN(progress)) {
    return 0;
  }
  if (progress >= 0 && progress <= 1) {
    return progress * 100;
  }

  return Math.max(0, Math.min(progress, 100));
};

export const PipelineTimestamps = () => {
  const { call } = useAPI();
  const { openModal, closeModal } = useModalContext();
  const {
    jobId,
    pipelineStatus,
    attempts,
    selectedAttemptId,
    attemptDetails,
    pipelineLoading,
    attemptLoading,
    pipelineError,
    loadPipeline,
    loadAttemptDetails,
    setSelectedAttemptId,
  } = usePageContext();
  const logsQuery = useQuery({
    queryKey: adminJobsKeys.attemptLogsByAttempt(selectedAttemptId ?? ''),
    queryFn: createFetchAttemptLogsQueryFn(call),
    enabled: selectedAttemptId !== null && selectedAttemptId !== '',
  });
  const detailedLogs = useMemo(() => parseAttemptLogLines(logsQuery.data?.logs), [logsQuery.data]);
  const logsLoading = selectedAttemptId !== null && logsQuery.isLoading;
  const logsError = selectedAttemptId !== null && logsQuery.isError ? 'Unable to load detailed logs.' : null;

  const markAttemptFailedMutation = useMutation({
    mutationFn: (attemptId: string) => call(() => PipelineAttemptsService.markAttemptFailed({ attemptId })),
    onSuccess: async () => {
      toast.success('Attempt marked as failed');
      await loadPipeline();
    },
    onError: () => {
      toast.error('Unable to update attempt status');
    },
  });
  const markingAttemptId = markAttemptFailedMutation.isPending ? markAttemptFailedMutation.variables ?? null : null;

  const orderedAttempts = useMemo(() => {
    return [...attempts].sort((a, b) => new Date(b.startedAt).getTime() - new Date(a.startedAt).getTime());
  }, [attempts]);

  const latestAttemptSucceeded = useMemo(() => isSuccessfulStatus(orderedAttempts[0]?.status), [orderedAttempts]);

  const attemptLabel = useMemo(() => {
    if (!selectedAttemptId) {
      return null;
    }
    const idx = orderedAttempts.findIndex((attempt) => attempt.attemptId === selectedAttemptId);
    if (idx === -1) {
      return `Attempt`;
    }
    return `Attempt ${orderedAttempts.length - idx}`;
  }, [orderedAttempts, selectedAttemptId]);

  const eta = pipelineStatus?.eta ?? null;
  const latestAttemptRunning = orderedAttempts[0]?.status?.toLowerCase() === 'started';

  // Estimates can wobble a point or two between polls; never let the bar go backwards within an attempt.
  const maxSeenProgressRef = useRef<{ attemptId: string | null; value: number }>({ attemptId: null, value: 0 });

  const progressPercent = useMemo(() => {
    if (latestAttemptSucceeded) {
      return 100;
    }
    const raw = normalizeProgress(eta?.percentage ?? pipelineStatus?.progress?.percentage);
    const attemptId = pipelineStatus?.currentAttemptId ?? null;
    if (maxSeenProgressRef.current.attemptId !== attemptId) {
      maxSeenProgressRef.current = { attemptId, value: raw };
    } else {
      maxSeenProgressRef.current.value = Math.max(maxSeenProgressRef.current.value, raw);
    }
    return maxSeenProgressRef.current.value;
  }, [latestAttemptSucceeded, eta?.percentage, pipelineStatus?.progress?.percentage, pipelineStatus?.currentAttemptId]);

  const showIndeterminateProgress = !latestAttemptSucceeded && latestAttemptRunning && !eta;

  const attemptDuration = useMemo(() => {
    if (attemptDetails?.performance?.totalDuration) {
      const totalDurationMs = attemptDetails.performance.totalDuration;
      return Math.round(totalDurationMs / 1000);
    }

    if (!attemptDetails?.startedAt) {
      return undefined;
    }

    const start = new Date(attemptDetails.startedAt).getTime();

    const end = attemptDetails.completedAt ? new Date(attemptDetails.completedAt).getTime() : Date.now();

    if (Number.isNaN(start) || Number.isNaN(end) || end < start) {
      return undefined;
    }

    return Math.round((end - start) / 1000);
  }, [attemptDetails]);

  const confirmMarkAttemptFailed = (attemptId: string) => {
    openModal(ModalNamesEnum.GenericConfirmation, {
      title: 'Mark attempt as failed',
      description: 'This will mark the current pipeline attempt as failed and mark the job as errored.',
      buttonText: 'Mark failed',
      onCancel: () => closeModal(),
      onSuccess: () => {
        closeModal();
        markAttemptFailedMutation.mutate(attemptId);
      },
    });
  };

  if (!jobId) {
    return null;
  }

  const eventsToRender: PipelineEventDto[] = attemptDetails?.events ?? pipelineStatus?.timeline ?? [];
  const hasAttempts = orderedAttempts.length > 0;
  const selectedAttemptActionId = attemptDetails?.attemptId ?? null;
  const selectedAttemptStatus = attemptDetails?.status?.toLowerCase();
  const canMarkSelectedAttemptFailed = Boolean(selectedAttemptActionId && selectedAttemptStatus === 'started');
  const isMarkingSelectedAttempt = Boolean(selectedAttemptActionId && markingAttemptId === selectedAttemptActionId);
  const handleMarkAttemptFailedClick = () => {
    if (!selectedAttemptActionId) {
      return;
    }
    confirmMarkAttemptFailed(selectedAttemptActionId);
  };

  return (
    <JobSection>
      <JobSectionLeft>Pipeline</JobSectionLeft>
      <FullWidthSectionRight>
        <div className="space-y-6 rounded-xl border border-slate-200 bg-[var(--color-surface)] p-6 shadow-sm">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <p className="text-xs font-semibold uppercase text-[var(--color-text-muted)]">Current stage</p>
              <p className="text-lg font-semibold text-[var(--color-text)]">
                {formatStageLabel(pipelineStatus?.currentStage) ?? '---'}
              </p>
              <p className="text-sm text-[var(--color-text-muted)]">
                State:{' '}
                <span className="font-medium text-[var(--color-text-subtle)]">
                  {pipelineStatus?.currentState ?? '---'}
                </span>
              </p>
              <p className="text-sm text-[var(--color-text-muted)]">
                Retry available:{' '}
                <span className="font-medium text-[var(--color-text-subtle)]">
                  {pipelineStatus?.canRetry ? 'Yes' : 'No'}
                </span>
              </p>
            </div>
            <div className="min-w-[200px] flex-1">
              <div className="flex items-center gap-2">
                <p className="text-xs font-semibold uppercase text-[var(--color-text-muted)]">Progress</p>
                {eta?.runningLong && !latestAttemptSucceeded ? (
                  <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-800">
                    Running long
                  </span>
                ) : null}
              </div>
              <div className="mt-2 h-2 overflow-hidden rounded-full bg-[var(--color-surface-muted)]">
                {showIndeterminateProgress ? (
                  <div className="h-2 w-1/3 animate-pulse rounded-full bg-[var(--color-text-muted)]" />
                ) : (
                  <div
                    className="h-2 rounded-full bg-[var(--color-text-muted)] transition-all"
                    style={{ width: `${progressPercent}%` }}
                  />
                )}
              </div>
              {showIndeterminateProgress ? (
                <p className="mt-1 text-sm text-[var(--color-text-subtle)]">In progress…</p>
              ) : (
                <p className="mt-1 text-sm text-[var(--color-text-subtle)]">
                  {progressPercent.toFixed(0)}%
                  {eta && !latestAttemptSucceeded
                    ? ` • ~${formatEtaMs(eta.estimatedTimeRemainingMs)} remaining (up to ${formatEtaMs(
                        eta.estimatedTimeRemainingP90Ms,
                      )})`
                    : ''}
                </p>
              )}
              {eta && !latestAttemptSucceeded ? (
                <p className="mt-0.5 text-xs text-[var(--color-text-muted)]">
                  Based on {eta.sampleSize} comparable job{eta.sampleSize === 1 ? '' : 's'} over the last{' '}
                  {eta.windowDays} days
                </p>
              ) : null}
            </div>
            <button
              type="button"
              className="inline-flex items-center justify-center rounded-md border border-slate-300 px-4 py-2 text-sm font-medium text-[var(--color-text-subtle)] transition hover:border-slate-400 hover:bg-[var(--color-surface-muted)] disabled:cursor-not-allowed disabled:opacity-60"
              onClick={loadPipeline}
              disabled={pipelineLoading}
            >
              {pipelineLoading ? 'Refreshing...' : 'Refresh status'}
            </button>
          </div>

          {pipelineError ? (
            <div className="rounded-md border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-700">
              {pipelineError}
            </div>
          ) : null}

          {pipelineStatus?.temporal?.length ? <LiveExecutionPanel workflows={pipelineStatus.temporal} /> : null}

          <div className="flex flex-col gap-6 lg:flex-row">
            <div className="lg:w-1/3">
              <div className="rounded-lg border border-slate-200 bg-[var(--color-surface-muted)] p-4">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-semibold text-[var(--color-text-subtle)]">Attempts</p>
                  <span className="text-xs font-medium text-[var(--color-text-muted)]">{orderedAttempts.length}</span>
                </div>
                <div className="mt-4 flex flex-col gap-3">
                  {pipelineLoading && !hasAttempts ? (
                    <p className="text-sm text-[var(--color-text-muted)]">Loading attempts…</p>
                  ) : null}
                  {!pipelineLoading && !hasAttempts ? (
                    <p className="text-sm text-[var(--color-text-muted)]">
                      No pipeline attempts recorded for this job yet.
                    </p>
                  ) : null}
                  {orderedAttempts.map((attempt, idx) => {
                    const isSelected = attempt.attemptId === selectedAttemptId;
                    const attemptNumber = orderedAttempts.length - idx;
                    const attemptDurationSeconds =
                      typeof attempt.duration === 'number' ? attempt.duration / 1000 : undefined;
                    return (
                      <button
                        key={attempt.attemptId}
                        type="button"
                        onClick={() => {
                          if (attempt.attemptId !== selectedAttemptId) {
                            setSelectedAttemptId(attempt.attemptId);
                            loadAttemptDetails(attempt.attemptId);
                          }
                        }}
                        disabled={attempt.attemptId === selectedAttemptId && attemptLoading}
                        className={`w-full rounded-lg border px-4 py-3 text-left transition ${
                          isSelected
                            ? 'border-indigo-500 bg-[var(--color-surface)] shadow'
                            : 'border-transparent bg-[var(--color-surface)] hover:border-slate-300 hover:shadow-sm'
                        }`}
                      >
                        <div className="flex items-center justify-between gap-2">
                          <p className="font-semibold text-[var(--color-text)]">Attempt {attemptNumber}</p>
                          <div className="flex flex-wrap justify-end gap-1">
                            <PipelineStatusTag status={attempt.status} className="!px-2 !py-0.5 text-xs font-semibold">
                              {attempt.status}
                            </PipelineStatusTag>
                            {attempt.autoRetried ? (
                              <TagMutedRounded className="!px-2 !py-0.5 text-xs font-semibold">
                                AUTO-RETRIED
                              </TagMutedRounded>
                            ) : null}
                          </div>
                        </div>
                        <p className="mt-1 text-xs text-[var(--color-text-muted)]">
                          Started {formatDateTime(attempt.startedAt)}
                        </p>
                        {attempt.completedAt ? (
                          <p className="text-xs text-[var(--color-text-muted)]">
                            Completed {formatDateTime(attempt.completedAt)}
                          </p>
                        ) : null}
                        <div className="mt-2 flex flex-wrap gap-4 text-xs text-[var(--color-text-muted)]">
                          <span>Events: {attempt.eventCount}</span>
                          {attemptDurationSeconds !== undefined ? (
                            <span>Duration: {formatDuration(attemptDurationSeconds)}</span>
                          ) : null}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>

            <div className="flex-1">
              <div className="rounded-lg border border-slate-200 p-4">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div>
                    <p className="text-xs font-semibold uppercase text-[var(--color-text-muted)]">Selected attempt</p>
                    <p className="text-lg font-semibold text-[var(--color-text)]">
                      {attemptLabel ?? 'No attempt selected'}
                    </p>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    {attemptDetails?.status ? (
                      <PipelineStatusTag status={attemptDetails.status} className="!px-3 !py-1 text-sm font-semibold">
                        {attemptDetails.status}
                      </PipelineStatusTag>
                    ) : null}
                    {attemptDetails?.autoRetried ? (
                      <TagMutedRounded className="!px-3 !py-1 text-sm font-semibold">AUTO-RETRIED</TagMutedRounded>
                    ) : null}
                    {canMarkSelectedAttemptFailed ? (
                      <button
                        type="button"
                        onClick={handleMarkAttemptFailedClick}
                        disabled={isMarkingSelectedAttempt}
                        className="inline-flex items-center rounded-md border border-red-300 px-3 py-1.5 text-sm font-semibold text-red-700 transition hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        {isMarkingSelectedAttempt ? 'Marking…' : 'Mark attempt failed'}
                      </button>
                    ) : null}
                  </div>
                </div>

                {attemptLoading && (
                  <p className="mt-4 text-sm text-[var(--color-text-muted)]">Loading attempt details…</p>
                )}

                {!attemptLoading && !attemptDetails && eventsToRender.length === 0 ? (
                  <p className="mt-4 text-sm text-[var(--color-text-muted)]">No timeline data is available yet.</p>
                ) : null}

                {attemptDetails ? (
                  <div className="mt-4 grid gap-4 text-sm text-[var(--color-text-subtle)] sm:grid-cols-2">
                    <div>
                      <p className="text-xs uppercase text-[var(--color-text-muted)]">Attempt ID</p>
                      <p className="font-medium text-[var(--color-text)]">{attemptDetails.attemptId}</p>
                    </div>
                    <div>
                      <p className="text-xs uppercase text-[var(--color-text-muted)]">Started</p>
                      <p className="font-medium text-[var(--color-text)]">{formatDateTime(attemptDetails.startedAt)}</p>
                    </div>
                    <div>
                      <p className="text-xs uppercase text-[var(--color-text-muted)]">Completed</p>
                      <p className="font-medium text-[var(--color-text)]">
                        {formatDateTime(attemptDetails.completedAt)}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs uppercase text-[var(--color-text-muted)]">Duration</p>
                      <p className="font-medium text-[var(--color-text)]">{formatDuration(attemptDuration)}</p>
                    </div>
                    <div>
                      <p className="text-xs uppercase text-[var(--color-text-muted)]">Events recorded</p>
                      <p className="font-medium text-[var(--color-text)]">{attemptDetails.events.length}</p>
                    </div>
                    {attemptDetails?.detailedLogsS3Key ? (
                      <div className="sm:col-span-2">
                        <p className="text-xs uppercase text-[var(--color-text-muted)]">Logs key</p>
                        <p className="font-medium text-[var(--color-text)] break-all">
                          {attemptDetails.detailedLogsS3Key}
                        </p>
                      </div>
                    ) : null}
                  </div>
                ) : null}

                {attemptDetails?.performance ? (
                  <div className="mt-4 rounded-md bg-[var(--color-surface-muted)] p-4">
                    <p className="text-sm font-semibold text-[var(--color-text-subtle)]">Performance</p>
                    <div className="mt-3 grid gap-4 text-sm text-[var(--color-text-subtle)] sm:grid-cols-3">
                      <div>
                        <p className="text-xs uppercase text-[var(--color-text-muted)]">Total duration</p>
                        <p className="font-medium text-[var(--color-text)]">
                          {formatDuration(Math.round(attemptDetails.performance.totalDuration / 1000))}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs uppercase text-[var(--color-text-muted)]">Average event</p>
                        <p className="font-medium text-[var(--color-text)]">
                          {formatDuration(Math.round(attemptDetails.performance.averageEventDuration / 1000))}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs uppercase text-[var(--color-text-muted)]">Error rate</p>
                        <p className="font-medium text-[var(--color-text)]">
                          {attemptDetails.performance.errorRate.toFixed(2)}
                        </p>
                      </div>
                    </div>
                  </div>
                ) : null}

                {attemptDetails?.errors?.length ? (
                  <div className="mt-4 rounded-md border border-[var(--color-error-border)] bg-[var(--color-error-surface)] p-4">
                    <p className="text-sm font-semibold text-[var(--color-error-heading)]">Errors</p>
                    <ul className="mt-3 space-y-3 text-sm text-[var(--color-error-body)]">
                      {attemptDetails.errors.map((error) => (
                        <li key={error.id} className="rounded border border-border bg-surface p-3">
                          <div className="flex flex-wrap items-center gap-2">
                            <p className="font-semibold text-[var(--color-error-body)]">
                              {formatServiceLabel(error.service)} • {formatStageLabel(error.stage)}
                            </p>
                            <PipelineStatusTag status={error.status} className="!px-2 !py-0.5 text-xs font-semibold">
                              {error.status}
                            </PipelineStatusTag>
                          </div>
                          {error.errorDetails ? (
                            <pre className="mt-2 overflow-auto rounded bg-surface-muted p-2 text-xs text-foreground whitespace-pre-wrap break-words">
                              {JSON.stringify(error.errorDetails, null, 2)}
                            </pre>
                          ) : null}
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : null}

                {eventsToRender.length ? (
                  <div className="mt-6">
                    <p className="text-sm font-semibold text-[var(--color-text-subtle)]">Event timeline</p>
                    <ul className="mt-4 space-y-6">
                      {eventsToRender.map((event, index) => {
                        const isDispatchRow = isDispatchStage(event.stage);
                        return (
                          <li key={event.id ?? `${event.stage}-${event.startedAt}-${index}`} className="flex gap-4">
                            <div className="w-44 text-right text-xs text-[var(--color-text-muted)]">
                              <p>{formatDateTime(event.startedAt)}</p>
                              {event.completedAt ? <p>{formatDateTime(event.completedAt)}</p> : null}
                            </div>
                            <div className="relative flex flex-col items-center">
                              <span
                                className={`mt-1 inline-block h-3 w-3 rounded-full ${eventDotColor(event.status)} ${
                                  isDispatchRow ? 'opacity-50' : ''
                                }`}
                              />
                              {index !== eventsToRender.length - 1 ? (
                                <span className="absolute top-4 bottom-[-24px] w-px bg-slate-200" />
                              ) : null}
                            </div>
                            <div
                              className={`flex-1 rounded-lg border border-[var(--color-surface-muted)] p-4 ${
                                isDispatchRow ? 'opacity-60' : ''
                              }`}
                            >
                              <div className="flex flex-wrap items-center gap-2">
                                <p
                                  className={
                                    isDispatchRow
                                      ? 'font-normal text-[var(--color-text-muted)]'
                                      : 'font-semibold text-[var(--color-text)]'
                                  }
                                >
                                  {formatServiceLabel(event.service)} / {formatStageLabel(event.stage)}
                                </p>
                                <PipelineStatusTag
                                  status={event.status}
                                  className="!px-2 !py-0.5 text-xs font-semibold"
                                >
                                  {event.status}
                                </PipelineStatusTag>
                              </div>
                              <p className="mt-1 text-xs text-[var(--color-text-muted)]">
                                Duration: {formatDuration(eventDurationSeconds(event))}
                              </p>
                              {event.metadata ? (
                                <details className="mt-2 text-sm text-[var(--color-text-subtle)]">
                                  <summary className="cursor-pointer text-xs uppercase text-[var(--color-text-muted)]">
                                    Metadata
                                  </summary>
                                  <pre className="mt-1 max-h-64 overflow-auto rounded bg-[var(--color-surface-muted)] p-2 text-xs text-[var(--color-text)] whitespace-pre-wrap break-words">
                                    {JSON.stringify(event.metadata, null, 2)}
                                  </pre>
                                </details>
                              ) : null}
                              {event.errorDetails ? (
                                <details className="mt-2 text-sm text-red-700">
                                  <summary className="cursor-pointer text-xs uppercase">Error details</summary>
                                  <pre className="mt-1 max-h-64 overflow-auto rounded bg-red-50 p-2 text-xs text-red-900 whitespace-pre-wrap break-words">
                                    {JSON.stringify(event.errorDetails, null, 2)}
                                  </pre>
                                </details>
                              ) : null}
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                ) : null}

                {selectedAttemptId && (detailedLogs.length > 0 || logsLoading || logsError) ? (
                  <div className="mt-6">
                    <p className="text-sm font-semibold text-[var(--color-text-subtle)]">Detailed event logs</p>
                    {logsLoading ? (
                      <p className="mt-4 text-sm text-[var(--color-text-muted)]">Loading detailed logs…</p>
                    ) : logsError ? (
                      <div className="mt-4 rounded-md border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-700">
                        {logsError}
                      </div>
                    ) : detailedLogs.length === 0 ? (
                      <p className="mt-4 text-sm text-[var(--color-text-muted)]">No detailed logs available.</p>
                    ) : (
                      <ul className="mt-4 space-y-3">
                        {detailedLogs.map((logEntry, index) => (
                          <li
                            key={index}
                            className="rounded-lg border border-slate-200 bg-[var(--color-surface-muted)]"
                          >
                            <details className="group">
                              <summary className="cursor-pointer px-4 py-3 text-sm font-medium text-[var(--color-text-subtle)] transition hover:bg-[var(--color-surface-muted)]">
                                <div className="flex items-center justify-between">
                                  <span>
                                    Event {index + 1}
                                    {logEntry.timestamp ? (
                                      <span className="ml-2 text-xs text-[var(--color-text-muted)]">
                                        {formatDateTime(String(logEntry.timestamp))}
                                      </span>
                                    ) : null}
                                  </span>
                                  <span className="text-xs text-[var(--color-text-muted)] group-open:hidden">▼</span>
                                  <span className="text-xs text-[var(--color-text-muted)] hidden group-open:inline">
                                    ▲
                                  </span>
                                </div>
                              </summary>
                              <div className="border-t border-slate-200 bg-[var(--color-surface)] px-4 py-3">
                                <pre className="max-h-96 overflow-auto rounded bg-[var(--color-surface-muted)] p-3 text-xs text-[var(--color-text)] whitespace-pre-wrap break-words">
                                  {JSON.stringify(logEntry, null, 2)}
                                </pre>
                              </div>
                            </details>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      </FullWidthSectionRight>
    </JobSection>
  );
};
